import React, { useLayoutEffect, useState } from 'react';
import PropTypes from 'prop-types';

import Loader from '../loader/loader';
import Login from '../login/login';

import {
  InventoryAuthData,
  SalesAuthData,
  ArrangementsAuthData,
  ApiResponseStatus,
} from '../../constants';
import {setPromiseAllSettled} from '../../utils';
import api from '../../api';

const initialAuthData = {
  userEmail: '',
  userEmailError: false,
  userEmailErrorMsg: '',
  userPassword: '',
  userPasswordError: false,
  userPasswordErrorMsg: '',
};

const UserModuleContainer = ({handleForgotPasswordButtonClick}) => {
  const [authData, setAuthData] = useState({...initialAuthData});
  const [isDataLoading, setIsDataLoading] = useState(true);
  const [isFormError, setIsFormError] = useState(false);

  const [isUserAuthorized, setIsUserAuthorized] = useState(false);

  useLayoutEffect(() => {
    const checkUserData = () => {
      const isInventoryAuthData = localStorage.getItem(InventoryAuthData.USER) && localStorage.getItem(InventoryAuthData.TOKEN);
      const isSalesAuthData = localStorage.getItem(SalesAuthData.USER) && localStorage.getItem(SalesAuthData.TOKEN);
      const isArrangementsAuthData = localStorage.getItem(ArrangementsAuthData.USER) && localStorage.getItem(ArrangementsAuthData.TOKEN);

      if (isInventoryAuthData || isSalesAuthData || isArrangementsAuthData) {
        setIsUserAuthorized(true);
      } else if (isUserAuthorized) {
        setIsUserAuthorized(false);
      }
      setIsDataLoading(false);
    };

    const testToken = async (tokenName) => {
      const token = localStorage.getItem(tokenName);
      if (!token) {
        return;
      }

      const url = tokenName === InventoryAuthData.TOKEN ? window.API_URL_INVENTORY : window.API_URL_SALES;

      await api.post(url, null, {
        headers: {
          Authorization: token,
        },
      })
        .catch((err) => {
          if (err?.response?.status === ApiResponseStatus.UNAUTHORIZED) {
            localStorage.removeItem(tokenName);
          }
        });
    };

    if (!Promise.allSettled) {
      setPromiseAllSettled();
    }

    Promise.allSettled([testToken(InventoryAuthData.TOKEN), testToken(SalesAuthData.TOKEN), testToken(ArrangementsAuthData.TOKEN)])
      .finally(() => {
        checkUserData();
      });
  }, []);

  const handleSignInButtonClick = (email, password) => {
    const getInventoryAuthorization = () => (
      api.post(`${window.API_URL_INVENTORY}/users/login/by_email_password`, {
        email: email.toLowerCase(),
        password,
      })
        .then((response) => {
          localStorage.setItem(InventoryAuthData.USER, JSON.stringify(response.data));
          localStorage.setItem(InventoryAuthData.TOKEN, response.headers.authorization);
        })
    );

    const getSalesAuthorization = () => (
      api.post(`${window.API_URL_SALES}/v1/users/login`, {
        email: email.toLowerCase(),
        password,
      })
        .then((response) => {
          localStorage.setItem(SalesAuthData.USER, JSON.stringify(response.data));
          localStorage.setItem(SalesAuthData.TOKEN, response.headers.authorization);

          if (window.API_URL_CREMATORIUM) { // while same url for sales and cremation is used
            localStorage.setItem(ArrangementsAuthData.USER, JSON.stringify(response.data));
            localStorage.setItem(ArrangementsAuthData.TOKEN, response.headers.authorization);
          }
        })
    );

    setIsDataLoading(true);

    if (!Promise.allSettled) {
      setPromiseAllSettled();
    }

    Promise.allSettled([getInventoryAuthorization(), getSalesAuthorization()])
      .then((results) => {
        if (results[0].status === 'rejected' && results[1].status === 'rejected') {
          const isFirstCallUnauthorized = results[0]?.reason?.response?.status === ApiResponseStatus.UNAUTHORIZED;
          const isSecondCallUnauthorized = results[1]?.reason?.response?.status === ApiResponseStatus.UNAUTHORIZED;
          if (isFirstCallUnauthorized || isSecondCallUnauthorized) {
            setIsFormError(true);
          }
        } else {
          setIsUserAuthorized(true);
        }
        setIsDataLoading(false);
      })
      .catch(() => {
        setIsDataLoading(false);
      });
  };

  if (isDataLoading) {
    return <Loader />;
  }

  if (isUserAuthorized) {
    window.location.href = window.APP_URL_DASHBOARD;
  }

  return (
    <Login
      authData={authData}
      handleForgotPasswordButtonClick={handleForgotPasswordButtonClick}
      handleSignInButtonClick={handleSignInButtonClick}
      isFormError={isFormError}
      removeFormError={() => setIsFormError(false)}
      setAuthData={setAuthData}
    />
  );
};

UserModuleContainer.propTypes = {
  handleForgotPasswordButtonClick: PropTypes.func.isRequired,
};

export default UserModuleContainer;
